import { getStartHour, hmmmToSlot, hmmmToEndSlot } from '@/config/modeConfig';
import type { OfficerMetadata } from '@/types/api';

// Matches OfficerScheduleGrid's break heuristic: a zero-run strictly BETWEEN two
// counter assignments shorter than this is a scheduled break, not "green" available
// time. A zero-run at the very start/end of an availability window (before the first
// counter or after the last) is never a break, however short — see greenSlotList.
const SHORT_BREAK_SLOTS = 12;

export interface UnassignedOfficer {
  key: string;
  label: string;
  green: number;
  type: string;
  /** Contiguous [startSlot, endSlot) blocks of vacant (green) time this officer has. */
  vacantBlocks: Array<[number, number]>;
}

/** Sorted list of green (available-but-unassigned) slot indices within `ranges`.
 *
 * A slot is green when it is unassigned, inside an availability range, and either the user
 * explicitly erased it (`cleared`) OR the length heuristic classifies it as availability
 * rather than a scheduled break — but never when the user explicitly marked it a break
 * (`userBreaks`). This mirrors exactly what OfficerScheduleGrid paints green, so the batch
 * auto-assign captures the same slots the officer grid shows as available. */
export function greenSlotList(
  schedule: number[],
  ranges: Array<[number, number]>,
  cleared?: Set<number>,
  userBreaks?: Set<number>,
): number[] {
  const green: number[] = [];
  for (const [s, e] of ranges) {
    const end = Math.min(e, schedule.length);
    const working: number[] = [];
    for (let i = s; i < end; i++) if ((schedule[i] ?? 0) !== 0) working.push(i);
    for (let i = s; i < end; i++) {
      if ((schedule[i] ?? 0) !== 0) continue;
      // User overrides win over the heuristic (they mirror the canvas exactly).
      if (userBreaks?.has(i)) continue;
      if (cleared?.has(i)) { green.push(i); continue; }
      if (working.length === 0) { green.push(i); continue; }
      const firstW = working[0];
      const lastW = working[working.length - 1];
      if (i < firstW) { green.push(i); continue; }
      if (i > lastW) { green.push(i); continue; }
      let runS = i, runE = i;
      while (runS > s && (schedule[runS - 1] ?? 0) === 0) runS--;
      while (runE < end - 1 && (schedule[runE + 1] ?? 0) === 0) runE++;
      if (runE - runS + 1 >= SHORT_BREAK_SLOTS) green.push(i);
    }
  }
  green.sort((a, b) => a - b);
  return green;
}

/** Return the contiguous [startSlot, endSlot) blocks of green slots within ranges. */
export function greenBlocks(
  schedule: number[],
  ranges: Array<[number, number]>,
  cleared?: Set<number>,
  userBreaks?: Set<number>,
): Array<[number, number]> {
  const green = greenSlotList(schedule, ranges, cleared, userBreaks);
  const blocks: Array<[number, number]> = [];
  for (const g of green) {
    const last = blocks[blocks.length - 1];
    if (last && g === last[1]) last[1] = g + 1;
    else blocks.push([g, g + 1]);
  }
  return blocks;
}

/** Availability ranges [startSlot, endSlot) for an officer, from segments or start/end. */
export function availRanges(meta: OfficerMetadata | undefined, startHour: number): Array<[number, number]> {
  if (!meta) return [];
  const ranges: Array<[number, number]> = [];
  if (meta.availability_segments && meta.availability_segments.length > 1) {
    for (const seg of meta.availability_segments) {
      try { ranges.push([hmmmToSlot(seg.start, startHour), hmmmToEndSlot(seg.end, startHour)]); } catch { /* skip */ }
    }
  } else if (meta.availability_start && meta.availability_end) {
    try { ranges.push([hmmmToSlot(meta.availability_start, startHour), hmmmToEndSlot(meta.availability_end, startHour)]); } catch { /* skip */ }
  }
  return ranges;
}

/**
 * Count the "green" (available-but-unassigned) slots for an officer — the slots a
 * shared auto-assign would fill (see greenSlotList for the exact rule).
 */
export function countGreenSlots(
  schedule: number[],
  ranges: Array<[number, number]>,
  cleared?: Set<number>,
  userBreaks?: Set<number>,
): number {
  return greenSlotList(schedule, ranges, cleared, userBreaks).length;
}

/** Per-officer slot overrides the user set in the officer grid: `cleared` slots (erased with
 *  press-and-hold) count as available even if short; `userBreaks` slots never do. */
export interface BreakOverrides {
  cleared?: Record<string, number[]>;
  userBreaks?: Record<string, number[]>;
}

/**
 * List the SOS/OT officers that currently have green (available-but-unassigned) time —
 * the officers a shared auto-assign could act on. Shared by the modal and the toolbar
 * button (which grays out when this list is empty). `overrides` folds in the user's
 * per-slot erase/break choices so the list matches what the officer grid paints green.
 */
export function officersWithUnassignedTime(
  officer_schedules: Record<string, number[]>,
  officer_metadata: Record<string, OfficerMetadata>,
  shiftType: string,
  overrides?: BreakOverrides,
  /** Free-text duties (RosterState.duty_slots): working time, so never green. */
  duty_slots?: Record<string, Record<string, string>>,
): UnassignedOfficer[] {
  const startHour = getStartHour(shiftType);
  const rows: UnassignedOfficer[] = [];
  for (const [key, meta] of Object.entries(officer_metadata)) {
    const t = (meta.officer_type ?? '').toUpperCase();
    if (t !== 'SOS' && t !== 'OT') continue;
    const plain = officer_schedules[key];
    if (!plain) continue;
    const sched = withDuties(plain, duty_slots?.[key]);
    const ranges = availRanges(meta, startHour);
    const cleared = overrides?.cleared?.[key] ? new Set(overrides.cleared[key]) : undefined;
    const userBreaks = overrides?.userBreaks?.[key] ? new Set(overrides.userBreaks[key]) : undefined;
    const green = countGreenSlots(sched, ranges, cleared, userBreaks);
    if (green <= 0) continue;
    const parts = [meta.deployment, meta.name].filter(Boolean).join(' – ');
    rows.push({ key, label: parts || key, green, type: t, vacantBlocks: greenBlocks(sched, ranges, cleared, userBreaks) });
  }
  rows.sort((a, b) => b.green - a.green);
  return rows;
}

/** `schedule` with each slot on a free-text duty marked as working (-2): the officer is busy
 *  there, though on no counter, so it is neither green nor the gap of a break. */
export function withDuties(schedule: number[], duties?: Record<string, string>): number[] {
  if (!duties) return schedule;
  const keys = Object.keys(duties);
  if (keys.length === 0) return schedule;
  const out = [...schedule];
  for (const k of keys) {
    const slot = Number(k);
    if (Number.isInteger(slot) && slot >= 0 && slot < out.length && (out[slot] ?? 0) === 0) out[slot] = -2;
  }
  return out;
}

/** Read the user's persisted break overrides (cleared/erased slots and explicit breaks) for
 *  the current shift + officer set from sessionStorage — the same store OfficerScheduleGrid
 *  writes. Returns empty maps if nothing is stored or storage is unavailable. */
export function readBreakOverrides(
  officer_schedules: Record<string, number[]>,
  shiftType: string,
): BreakOverrides {
  try {
    const base = `ac_brk_${shiftType}_${Object.keys(officer_schedules).sort().join(',')}`;
    const parse = (suffix: string): Record<string, number[]> => {
      const raw = sessionStorage.getItem(`${base}_${suffix}`);
      return raw ? (JSON.parse(raw) as Record<string, number[]>) : {};
    };
    return { cleared: parse('c'), userBreaks: parse('u') };
  } catch {
    return {};
  }
}
